import { FC, memo, useEffect, useMemo, useState } from 'react';
import { Link, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Colors, useBreakpoint, useIsMobile } from '@brg/ui';
import { API, Project, Tag, Endpoints, ProjectAPI } from '@brg/api';
import { ProfileUtils, useAssociatedProfile } from 'core/auth';
import styled, { useTheme, css } from 'styled-components';
import { ProjectEditFrameForbidden, ProjectFrameError } from '../../components/error-frames';
import ProjectDisplayLayout from '../../components/template/project-display-layout';
import { computeProject } from '../../helpers/types/computed/ComputedProject';
import { ErrorNotAllowed } from '../../components/error-frames/error-not-allowed';
import { Overback } from '../../components/template/page-template';
import { ImageRequest } from '../../constants/imageRequest';
import { ProjectStatusDeleted, ProjectStatusDrafts } from '../../helpers/projectStatus';
import { LayoutConst } from '../../../core';
import { AppTester } from '../../../supporting';
import { createThemeByColor } from '../../helpers/coloring';
import { InfinityProjectsActions } from 'shared/reducers/InfinityProjects';
import { useDispatch, useSelector } from 'react-redux';
import { NavbarSearchActions } from "../../reducers/NavbarSearch";
import { setIsOutsideTextPosition, setProjectVType } from "../../reducers/ViewTypeSlice";
import { useGetCurrentTeam } from "../../hooks/useGetCurrentTeam";
import { useGetCompetitionList } from "../../hooks/api/useGetCompetitionList";
import { ErrorNotPreviewPermission } from 'shared/components/error-frames/error-not-preview-permission';
import { BookFrameError } from '_deziiign/books/core/components/error-frames/book-edit-frames';
import { getLongreadCoverTextPosition } from 'shared/helpers/getCoverTextPosition';
import useQueryState from 'shared/hooks/useQueryState';
import { SP_VOTE_COLLECTION_SIGN } from '../VotesAdmin/constants';
import { CurrentProjectActions, getOstbay } from "../../reducers/CurrentProject";
import NotFoundPage from "../notfound";
import { ModerationType } from "../teams/const/ModerationTypes";
import { VoteEnd } from 'shared/components/frames/VoteEnd';
import { PROJECT_VOTE_STATUS, useVoteContest } from 'core/providers/VoteProvider';
import { SCROLL_POSITION, useScrollByAnchorElement } from "../../hooks/useScrollByAnchorElement";
import useInstantScroll from "shared/hooks/useSmoothScroll";
import { useGetLMSModule } from "../../hooks/api/useGetLMSModule";
import { useGetLMSDisciplines } from "../../hooks/api/LMS/useGetLMSDisciplines";
import { useGetBookPaymentData } from "../../hooks/useGetBookPaymentData";
import { useGetProject } from "../../hooks/api/useGetProject";
import { useDeepCompareEffect } from "../../utils/useDeepCompareEffect";
import ProjectContent from "./projectContent";
import Unavailable from '../unavailable';
import { BookDelegate } from '_deziiign/books/core/book-delegate';
import { useGetFriendlyUrlParam } from 'shared/hooks/useGetFriendlyUrlParam';
import chapterPagerSlice from 'shared/reducers/ChapterPagerSlice';
import { executeLmsButtonPath } from 'shared/utils/executeLmsButtonPath';
import { BlockedProjectModal } from 'shared/modals/BlockedProjectModal';

export const sortTags = (tags: Tag[]) => {
    //@ts-ignore
    const { educational, rest } = tags.reduce((res, tag) => {
        //@ts-ignore
        if (tag?.isEducationTag) {
            return {
                ...res,
                educational: [...res.educational, tag],
            }
        }
        return {
            ...res,
            rest: [...res.rest, tag],
        }
    }, {
        educational: [],
        rest: [],
    });
    //@ts-ignore
    const { cities, notCities } = rest.reduce((res, tag) => {
        if (tag?.entityTypes?.includes('cities')) {
            return {
                ...res,
                cities: [...res.cities, tag],
            }
        }
        return {
            ...res,
            notCities: [...res.notCities, tag],
        }
    }, {
        cities: [],
        notCities: [],
    });
    //@ts-ignore
    const { places, notPlaces } = notCities.reduce((res, tag) => {
        if (tag?.entityTypes?.includes('places')) {
            return {
                ...res,
                places: [...res.places, tag],
            }
        }
        return {
            ...res,
            notPlaces: [...res.notPlaces, tag],
        }
    }, {
        places: [],
        notPlaces: [],
    });
    return [...educational, ...cities, ...places, ...notPlaces]

}

interface PageProps {
    className?: string;
    projectId?: string;
    isLocalPreview?: boolean;
}

export const Title = styled.h1<{ isAgeytomesh?: boolean, isProjectEdit?: boolean, maxTitleWidth?: number, visibleTextColor?: string }>`
  font-family: Inter, sans-serif;
  margin: 0;
  font-size: 30px;
  line-height: 33px;
  font-weight: 500;
  letter-spacing: -0.02em;
  color: ${({ visibleTextColor }) => visibleTextColor};
  max-width: ${LayoutConst.projectCover.l.w}px;

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    font-size: 23px;
    line-height: 27px;
    max-width: ${LayoutConst.projectCover.m.w}px;
  }

  @media (max-width: 940px) {
    max-width: ${({ maxTitleWidth }) => maxTitleWidth || 452}px;
    overflow: hidden;
    text-overflow: ellipsis;
    ${({ isAgeytomesh }) => isAgeytomesh ? css`
      color: #545453;
    ` : null} // color: ${({ theme }) => theme.projectView.titleColorMobile};
  }

  @media (max-width: ${LayoutConst.screen.s - 1}px) {
    max-width: 100%;
    ${({ isProjectEdit }) => isProjectEdit ? css`
      font-size: 22px;
      line-height: 25px;
      font-weight: 500;
    ` : null};
  }
`;

export const LimitedLink = styled(Link) <{ disabled?: boolean }>`
  max-width: 100%;
  line-height: 0.8em;
  margin-bottom: 0px !important;
  margin-right: 0px !important;

  a {
    ${p => p.disabled && 'pointer-event: none;'}
  }
  

  @media (max-width: ${LayoutConst.screen.m}px) {
    //margin-bottom: 8px !important;
    &:last-child {
      //margin-bottom: px !important;
    }
  }
`

export const StatusPseudoTag = styled.div<any>`
  display: flex;
  padding: 4px 16px;
  border: 1px solid transparent;
  justify-content: center;
  align-items: center;
  border-radius: 5px;
  background: #D81700;
  color: #FEFEFE;
  font-family: Inter;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.3px;
//   margin-bottom: ${p => p.mb !== undefined ? `${p.mb}px!important` : 0};
//   margin-right: ${p => p.mr !== undefined ? `${p.mr}px!important` : '16px'};
  margin: 0 0 ${p => p.mb !== undefined ? `${p.mb}px!important` : 0} 0;

    @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
        font-size: 13px;
        line-height: 16px;
    }

    @media (max-width: ${LayoutConst.screen.m}px) {
        margin-bottom: ${p => p.mb !== undefined ? `${p.mb}px!important` : 0};
        margin-right: ${p => p.mr !== undefined ? `${p.mr}px!important` : '8px'};
    }

    @media (max-width: ${LayoutConst.screen.s - 1}px) {
        padding: 4px 12px;
        margin: 0 0 8px 0;
    }
`

export const EmptyLink = styled.div`
  max-width: 100%;
  line-height: 0.8em;
  margin-bottom: 0 !important;
  margin-right: 0 !important;
  text-decoration: none !important;

  @media (max-width: ${LayoutConst.screen.m}px) {
    &:last-child {
      margin-bottom: 6px !important;
    }
  }
`;

const ProjectPageNew: FC<PageProps> = ({ className, projectId, isLocalPreview }) => {
    const paramsId = useGetFriendlyUrlParam("id");
    const id = projectId || paramsId;
    const theme1 = useTheme();
    const isMobile = useIsMobile();
    const [t, setT] = useState(Date.now());
    const dispatch = useDispatch();
    const profile = useAssociatedProfile();
    const { project: loadedProject, isLoading: isProjectLoading, error: projectError } = useGetProject(id, id + t)
    const [projectSource, setProjectSource] = useState<any>(undefined)
    const [project, setProject] = useState<Project>();
    const [projectStatusFromList, setProjectStatusFromList] = useState<number | null>(null);
    const [hasLgGoods, setHasLgGoods] = useState(false);
    const [lgCoverTextPost, setLgCoverTextPos] = useState<string[]>([]);
    const { i18n } = useTranslation();
    const isProjectPreview = !!useRouteMatch("/preview");
    const isProjectInBookPreview = !!useRouteMatch(["/books/preview", "/book-preview"]);
    const isPreview = isProjectPreview || isProjectInBookPreview || isLocalPreview;
    const isBookProjectPreview = !!useRouteMatch(["/books/book-preview", '/book-preview']);
    const { voteEnd, onToggleStatus } = useVoteContest();
    //const history = useHistory();

    // useEffect(() => {
    //     if (!projectId && !!project && !!urlPath) {
    //         if (!!project.friendlyURLPath && project.friendlyURLPath !== urlPath) {
    //             history.replace(`${CreateLinkTo.projectNew(project)}${window.location.search}${window.location.hash}`);
    //         }
    //     }
    // }, [project, projectId, history, urlPath]);

    const computedProject = useMemo(() => computeProject(project, ImageRequest.image), [project]);

    const isMac13 = useBreakpoint(LayoutConst.screen.mac13);
    const is992 = useBreakpoint(LayoutConst.screen.m);
    const is744 = useBreakpoint(LayoutConst.screen.s);
    const is743 = useBreakpoint(743);
    const is1200 = useBreakpoint(LayoutConst.screen.l);
    const vtype = useSelector((state: any) => state.vt.projectVType);
    const isOutsideTextPosition = useSelector((state: any) => state.vt.isOutsideTextPosition);

    const currentTeam = useGetCurrentTeam();

    const isBooks = AppTester.isBooks();
    const isAgeyTomesh = AppTester.isAgeytomesh();
    const isOnlines = AppTester.isOnlines();
    const isSuperPuper = AppTester.isSuperPuper();
    const isGeograffee = AppTester.isGeograffee();
    const isCGrave = AppTester.isCGRAVE();
    const isMediiia = AppTester.isMediiia();
    const isAdcrDafesNet = AppTester.isAdcrDafesNet();
    const isContinuumSite = AppTester.isContinuumSite();
    const isGameDeziiign = AppTester.isGameDeziiign();
    const isDafes = AppTester.isGeoDafes();
    const isProHSE = AppTester.isProHSE();

    const isPaidBook: any = useMemo(() => BookDelegate.isPaid(project?.book || project) && (project?.isBook || project?.book), [project])

    const bookPaymentData = useGetBookPaymentData(project)
    // const {additionalItems: collectionReprod} = useGetBookPaymentData(project, true)
    const { additionalItems, isLoadingCard } = bookPaymentData

    // const [collectionReprod] = useGetAdditionalItems(project?.book?.bookId || project?.projectId, isPaidBook, Boolean(project?.book?.bookId || project?.isBook))


    // @ts-ignore
    const hasCreators = Boolean(project?.entitiesCreators?.length)
    const creatorsLoading = isProjectLoading

    const currentTeamId = useMemo(() => {
        // @ts-ignore
        const teams = project?.teams;
        if (!teams || teams?.length === 0) return '';

        return teams?.find((team: any) => team?.moderationType === ModerationType.LMS)?.teamId || ''
    }, [project])
    const { colors: disciplineColors } = useGetLMSDisciplines(currentTeamId)

    
    const { scrollToElement } = useInstantScroll({
        navbarHeight: 64,
        anchorMenuHeight: is744 ? 0 : 50,
        extraOffset: is744 ? 70 : 16,
        minDuration: 2000,
        maxDuration: 2000,
        scrollSpeed: 1.0,
    });

    useEffect(() => {
        const handleInitialScroll = () => {
            setTimeout(() => {
                const hash = window.location.hash.replace('#', '');
                if (hash) {
                    scrollToElement(hash, false);
                }
            }, 100);
        };

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', handleInitialScroll);
        } else {
            handleInitialScroll();
        }

        return () => {
            document.removeEventListener('DOMContentLoaded', handleInitialScroll);
        };
    }, [scrollToElement]);

    const competitionIds = useMemo(() => {
        //@ts-ignore
        return project?.competitionApproves?.map(({ competitionId }) => competitionId);
    }, [project])

    const isLmsModerationType = useMemo(
        //@ts-ignore
        () => (project?.teams?.length && project?.teams.some((i) => i?.moderationType === ModerationType.LMS)), [project]);

    const isBookView = useRouteMatch("/books/book");

    const setPagerContent = (project: Project) => {
        dispatch(chapterPagerSlice.actions.setChapter(project))
    }

    const [{ voteCollectionId, voteEndParam, currentCompetitionId, tab }, setQueryParams] = useQueryState();

    const isLMS = useMemo(
        () => ((project?.contexts?.includes('lmshseproject') || project?.contexts?.includes('lmsproject')) && isLmsModerationType), [project, isLmsModerationType])

    const computeAdditionaLmsData = useMemo(() => {
    return {
        //@ts-ignore
        contentLink: project?.additionalInfo?.contentLink || '',
             //@ts-ignore
        detailsLink: executeLmsButtonPath(project?.teams?.[0]?.buttons) || '',
             //@ts-ignore
        backLink: project?.teams?.[0]?.route || ''
    }
    } , [project])


    const {
        LMSModuleInfo: lmsModuleInfo,
        isLoading: lmsModuleInfoLoading
    } = useGetLMSModule(project?.projectId || "", computeAdditionaLmsData, !isLMS)

    const isOwner = useMemo(() => {
        if (!project) return undefined
        return profile ? (project?.authorId === profile?.accountId || project?.coAuthorIds?.includes(profile?.accountId) || (lmsModuleInfo?.teacherIds || [])?.includes(profile?.accountId)) : false
    }, [project, profile, lmsModuleInfo])

    const isModer = useMemo(() => profile && (ProfileUtils.isModer(profile) || ProfileUtils.isMiddleModer(profile)), [profile]);
    const isAuthorized = !!profile;

    const {
        competitionsWinner,
        competitionsParticipant,
        competitionInfo,
        competitionFynalist,
        competitionLMSInfo,
        competitionsExamCompleted
    } = useGetCompetitionList(competitionIds, id, isOwner || isModer, t);

    const isOwnerLoading = useMemo(() => (isOwner !== undefined && (!isLMS && !lmsModuleInfoLoading)), [isOwner, isLMS, lmsModuleInfo, lmsModuleInfoLoading]);

    const isProjectVotingOver = useMemo(
        () => (voteEnd === PROJECT_VOTE_STATUS.end && !voteEndParam && currentCompetitionId)
        , [voteEnd, voteEndParam, currentCompetitionId]);

    const isExamProject = useMemo(() => !!competitionLMSInfo?.length, [competitionLMSInfo]);

    const showVoteEnd = useMemo(() => 
        !isExamProject && 
        isProjectVotingOver && 
        competitionInfo && 
        competitionInfo.id && 
        competitionInfo.name, 
        [isExamProject, isProjectVotingOver, competitionInfo]
    );

    const onUpdate = (t: number) => setT(t);

    const isCollectionVote = useMemo(() => (project?.contexts?.includes(SP_VOTE_COLLECTION_SIGN)), [project])

    const usercolor = useMemo(() => {
        const p: any = project;
        if (project && p.additionalInfo && p.additionalInfo.color) {
            return p.additionalInfo.color;
        }
        return (AppTester.isDeziiign() || isDafes) ? Colors.gal : Colors.geo;
    }, [project, isDafes]);

    const calculatedColor = useMemo(() => {
        if (isMobile && hasCreators && isMediiia) {
            return '#e7e7e7';
        }
        if (isLMS) {
            return '#d2d4d3';
        }
        if (isGeograffee && !isDafes) {
            return Colors.geo;
        }
        if (isMobile && !(isSuperPuper || isCollectionVote || isCGrave || isContinuumSite || isAdcrDafesNet || isMediiia)) {
            return Colors.gal;
        }
        if (isSuperPuper || isCollectionVote || isContinuumSite || isAdcrDafesNet) {
            return '#000000';
        }

        if (isGameDeziiign && usercolor === Colors.geoWhite100) {
            return '#2A3034'
        }
        return usercolor;
    }, [isGeograffee, isMobile, hasCreators, usercolor, isCGrave, isDafes, isContinuumSite, isCollectionVote, isGameDeziiign, isMediiia]);

    const { theme } = createThemeByColor(calculatedColor, theme1);

    console.log('112112', calculatedColor)

    useEffect(() => {
        if (project?.author) {
            if (project?.author?.globalTypes?.includes('Employee')
                || project?.author?.globalTypes?.includes('Cooperator')
                || project?.author?.globalTypes?.includes('Student')
                || project?.author?.globalTypes?.includes('hsealumni')) {
                dispatch(NavbarSearchActions.setDesignerType(true));
            } else {
                dispatch(NavbarSearchActions.setDesignerType(false));
            }
        }
        const viewType = project?.longread?.viewType || 'container';
        dispatch(setProjectVType(viewType))
        return () => {
            dispatch(NavbarSearchActions.setDesignerType(true));
            dispatch(setProjectVType('container'))
        }
    }, [project?.author])

    useEffect(() => {
        if (project) {
            dispatch(InfinityProjectsActions.setIsProjectPage(true))
            dispatch(CurrentProjectActions.setOriginalsState(false))
            dispatch(CurrentProjectActions.setIsOstBay(getOstbay(project)))
        }
    }, [project])

    useEffect(() => {
        //@ts-ignore
        const teamId = project?.teams?.[0]?.teamId;

        if (teamId) {
            API.request()
                .endpoint(Endpoints.cardsMnePw)
                .url('/api/Products/GetList')
                .get()
                .addQuery('teamId', teamId)
                .addQuery('isHidden', 'false')
                .send()
                .then((r) => r.json())
                .then((r) => {
                    setHasLgGoods(r?.data?.length > 0)
                })
        }
    }, [project])

    useEffect(() => {
        if (loadedProject) {
            setProjectSource(loadedProject)
            setPagerContent(loadedProject)
        }
    }, [loadedProject]);

    useEffect(() => {
        if (projectError === 403 && id && !projectStatusFromList) {
            ProjectAPI.GetList({
                projectIds: [id],
                projectFilter: 1,
                isLite: true,
                isContextIgnored: true,
                limit: 1
            })
                .then((r) => r.json())
                .then((r) => {
                    if (r?.data && Array.isArray(r.data) && r.data.length > 0) {
                        const projectFromList = r.data[0];
                        if (projectFromList && typeof projectFromList === 'object' && projectFromList.status !== undefined) {
                            setProjectStatusFromList(projectFromList.status);
                        }
                    }
                })
                .catch(() => {});
        }
    }, [projectError, id, projectStatusFromList]);

    const getCoverTextPosition = (id: string) => {
        if (id) {
            API.request()
                .endpoint(Endpoints.longread)
                .url('api/post/GetMany')
                .addQuery('idUser', '2')
                .addQuery('idLongread', id)
                .get()
                .send()
                .then((r) => r.json())
                .then((r) => setLgCoverTextPos(
                    getLongreadCoverTextPosition(r)
                ))
        }
    }

    useEffect(() => {
        dispatch(setIsOutsideTextPosition(lgCoverTextPost?.includes('outside') || !lgCoverTextPost || lgCoverTextPost?.length === 0));
    }, [lgCoverTextPost, dispatch])

    useEffect(() => {
        if (project) {
            getCoverTextPosition(project?.longreadId)
        }
    }, [project])

    useDeepCompareEffect(() => {
        setProject(API.toLang(projectSource, i18n.language));
    }, [projectSource, i18n.language]);

    if (showVoteEnd) {
        return <VoteEnd text={competitionInfo?.name} link={competitionInfo?.id}
            // @ts-ignore
            onToggleStatus={(flag: boolean) => {
                setQueryParams?.({ voteEndParam: true })
                onToggleStatus(flag)
            }} />
    }

    if (isPreview && projectError === 403) {
        return <ProjectEditFrameForbidden id={id} />;
    }

    if (((isPreview || isBookProjectPreview) && isOwnerLoading && !isOwner && !isModer)) {
        return <ErrorNotPreviewPermission id={id} />;
    }

    if (isProjectLoading) {
        return (
            <Overback
                className={className}
                state={isProjectLoading}
                component={ProjectDisplayLayout}
            />
        );
    }

    const projectToCheck = project || loadedProject;
    if (projectToCheck && typeof projectToCheck === 'object' && projectToCheck.status === 3 && projectToCheck.projectId && !isOwner && !isModer) {
        return <BlockedProjectModal />;
    }

    if (ProjectStatusDeleted(project)) {
        return <Unavailable />;
    }

    if (
        project && (ProjectStatusDrafts(project)) &&
        (!isPreview && !isBookProjectPreview && isOwnerLoading && isOwner) &&
        !isModer) {
        return <ProjectEditFrameForbidden id={id} />;
    }
    
    if (projectToCheck && typeof projectToCheck === 'object' && 
        projectToCheck.status === 3 && 
        projectToCheck.projectId && 
        !isOwner && !isModer) {
        return <BlockedProjectModal />;
    }

    if (ProjectStatusDeleted(project)) {
        return <Unavailable />;
    }

    if (projectError === 403) {
        if (projectStatusFromList === 0) {
            if (isBookView) return <BookFrameError id={id} />
            return <ProjectFrameError id={id} />;
        }
        
        if (projectStatusFromList === 3) {
            return <BlockedProjectModal />;
        }
        
        if (isBookView) return <BookFrameError id={id} />
        return <ProjectFrameError id={id} />;
    }
    
    if (projectError === 404) {
        const projectToCheck = project || loadedProject;
        if (projectToCheck && typeof projectToCheck === 'object' && projectToCheck.status === 3 && projectToCheck.projectId) {
            return <BlockedProjectModal />;
        }
        return <NotFoundPage />
    }
    
    if (projectError) {
        if (isBookView) return <BookFrameError id={id} />
        return <ProjectFrameError id={id} />;
    }

    if (!project) {
        return null;
    }
    if (project.status === 404) {
        return <Unavailable />;
    }

    return (
        <ProjectContent
            projectId={id}
            project={project}
            setProject={setProject}
            tab={tab}
            voteCollectionId={voteCollectionId}
            voteEndParam={voteEndParam}
            currentCompetitionId={currentCompetitionId}
            setQueryParams={setQueryParams}
            isProjectLoading={isProjectLoading}
            projectSource={projectSource}
            computedProject={computedProject}
            setProjectSource={setProjectSource}
            isOwner={isOwner}
            isModer={isModer}
            isOwnerLoading={isOwnerLoading}
            profile={profile}
            lmsModuleInfo={lmsModuleInfo}
            isBookProjectPreview={isBookProjectPreview}
            isProjectPreview={isProjectPreview}
            isPreview={isPreview}
            competitionsExamCompleted={competitionsExamCompleted}
            competitionsParticipant={competitionsParticipant}
            competitionsWinner={competitionsWinner}
            competitionFynalist={competitionFynalist}
            isMac13={isMac13}
            is992={is992}
            is744={is744}
            is743={is743}
            is1200={is1200}
            vtype={vtype}
            isOutsideTextPosition={isOutsideTextPosition}
            onUpdate={onUpdate}
            hasLgGoods={hasLgGoods}
            lgCoverTextPost={lgCoverTextPost}
            currentTeam={currentTeam}
            bookPaymentData={bookPaymentData}
            additionalItems={additionalItems}
            collectionReprod={additionalItems}
            isBooks={isBooks}
            isAgeyTomesh={isAgeyTomesh}
            isOnlines={isOnlines}
            isSuperPuper={isSuperPuper}
            isGeograffee={isGeograffee}
            isCGrave={isCGrave}
            isAdcrDafesNet={isAdcrDafesNet}
            isContinuumSite={isContinuumSite}
            isGameDeziiign={isGameDeziiign}
            isDafes={isDafes}
            isProHSE={isProHSE}
            isLoadingCard={isLoadingCard}
            disciplineColors={disciplineColors}
            theme={theme}
            calculatedColor={calculatedColor}
            isCollectionVote={isCollectionVote}
            isMobile={isMobile}
            isLmsModerationType={isLmsModerationType}
            isLMS={isLMS}
            onlyContent={!!projectId}
        />
    )
};

export default memo(ProjectPageNew);
import { FC, memo, useEffect, useMemo, useState } from 'react';
import { Link, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Colors, useBreakpoint, useIsMobile } from '@brg/ui';
import { API, Project, Tag, Endpoints, ProjectAPI } from '@brg/api';
import { ProfileUtils, useAssociatedProfile } from 'core/auth';
import styled, { useTheme, css } from 'styled-components';
import { ProjectEditFrameForbidden, ProjectFrameError } from '../../components/error-frames';
import ProjectDisplayLayout from '../../components/template/project-display-layout';
import { computeProject } from '../../helpers/types/computed/ComputedProject';
import { ErrorNotAllowed } from '../../components/error-frames/error-not-allowed';
import { Overback } from '../../components/template/page-template';
import { ImageRequest } from '../../constants/imageRequest';
import { ProjectStatusDeleted, ProjectStatusDrafts } from '../../helpers/projectStatus';
import { LayoutConst } from '../../../core';
import { AppTester } from '../../../supporting';
import { createThemeByColor } from '../../helpers/coloring';
import { InfinityProjectsActions } from 'shared/reducers/InfinityProjects';
import { useDispatch, useSelector } from 'react-redux';
import { NavbarSearchActions } from "../../reducers/NavbarSearch";
import { setIsOutsideTextPosition, setProjectVType } from "../../reducers/ViewTypeSlice";
import { useGetCurrentTeam } from "../../hooks/useGetCurrentTeam";
import { useGetCompetitionList } from "../../hooks/api/useGetCompetitionList";
import { ErrorNotPreviewPermission } from 'shared/components/error-frames/error-not-preview-permission';
import { BookFrameError } from '_deziiign/books/core/components/error-frames/book-edit-frames';
import { getLongreadCoverTextPosition } from 'shared/helpers/getCoverTextPosition';
import useQueryState from 'shared/hooks/useQueryState';
import { SP_VOTE_COLLECTION_SIGN } from '../VotesAdmin/constants';
import { CurrentProjectActions, getOstbay } from "../../reducers/CurrentProject";
import NotFoundPage from "../notfound";
import { ModerationType } from "../teams/const/ModerationTypes";
import { VoteEnd } from 'shared/components/frames/VoteEnd';
import { PROJECT_VOTE_STATUS, useVoteContest } from 'core/providers/VoteProvider';
import { SCROLL_POSITION, useScrollByAnchorElement } from "../../hooks/useScrollByAnchorElement";
import useInstantScroll from "shared/hooks/useSmoothScroll";
import { useGetLMSModule } from "../../hooks/api/useGetLMSModule";
import { useGetLMSDisciplines } from "../../hooks/api/LMS/useGetLMSDisciplines";
import { useGetBookPaymentData } from "../../hooks/useGetBookPaymentData";
import { useGetProject } from "../../hooks/api/useGetProject";
import { useDeepCompareEffect } from "../../utils/useDeepCompareEffect";
import ProjectContent from "./projectContent";
import Unavailable from '../unavailable';
import { BookDelegate } from '_deziiign/books/core/book-delegate';
import { useGetFriendlyUrlParam } from 'shared/hooks/useGetFriendlyUrlParam';
import chapterPagerSlice from 'shared/reducers/ChapterPagerSlice';
import { executeLmsButtonPath } from 'shared/utils/executeLmsButtonPath';
import { BlockedProjectModal } from 'shared/modals/BlockedProjectModal';

export const sortTags = (tags: Tag[]) => {
    //@ts-ignore
    const { educational, rest } = tags.reduce((res, tag) => {
        //@ts-ignore
        if (tag?.isEducationTag) {
            return {
                ...res,
                educational: [...res.educational, tag],
            }
        }
        return {
            ...res,
            rest: [...res.rest, tag],
        }
    }, {
        educational: [],
        rest: [],
    });
    //@ts-ignore
    const { cities, notCities } = rest.reduce((res, tag) => {
        if (tag?.entityTypes?.includes('cities')) {
            return {
                ...res,
                cities: [...res.cities, tag],
            }
        }
        return {
            ...res,
            notCities: [...res.notCities, tag],
        }
    }, {
        cities: [],
        notCities: [],
    });
    //@ts-ignore
    const { places, notPlaces } = notCities.reduce((res, tag) => {
        if (tag?.entityTypes?.includes('places')) {
            return {
                ...res,
                places: [...res.places, tag],
            }
        }
        return {
            ...res,
            notPlaces: [...res.notPlaces, tag],
        }
    }, {
        places: [],
        notPlaces: [],
    });
    return [...educational, ...cities, ...places, ...notPlaces]

}

interface PageProps {
    className?: string;
    projectId?: string;
    isLocalPreview?: boolean;
}

export const Title = styled.h1<{ isAgeytomesh?: boolean, isProjectEdit?: boolean, maxTitleWidth?: number, visibleTextColor?: string }>`
  font-family: Inter, sans-serif;
  margin: 0;
  font-size: 30px;
  line-height: 33px;
  font-weight: 500;
  letter-spacing: -0.02em;
  color: ${({ visibleTextColor }) => visibleTextColor};
  max-width: ${LayoutConst.projectCover.l.w}px;

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    font-size: 23px;
    line-height: 27px;
    max-width: ${LayoutConst.projectCover.m.w}px;
  }

  @media (max-width: 940px) {
    max-width: ${({ maxTitleWidth }) => maxTitleWidth || 452}px;
    overflow: hidden;
    text-overflow: ellipsis;
    ${({ isAgeytomesh }) => isAgeytomesh ? css`
      color: #545453;
    ` : null} // color: ${({ theme }) => theme.projectView.titleColorMobile};
  }

  @media (max-width: ${LayoutConst.screen.s - 1}px) {
    max-width: 100%;
    ${({ isProjectEdit }) => isProjectEdit ? css`
      font-size: 22px;
      line-height: 25px;
      font-weight: 500;
    ` : null};
  }
`;

export const LimitedLink = styled(Link) <{ disabled?: boolean }>`
  max-width: 100%;
  line-height: 0.8em;
  margin-bottom: 0px !important;
  margin-right: 0px !important;

  a {
    ${p => p.disabled && 'pointer-event: none;'}
  }
  

  @media (max-width: ${LayoutConst.screen.m}px) {
    //margin-bottom: 8px !important;
    &:last-child {
      //margin-bottom: px !important;
    }
  }
`

export const StatusPseudoTag = styled.div<any>`
  display: flex;
  padding: 4px 16px;
  border: 1px solid transparent;
  justify-content: center;
  align-items: center;
  border-radius: 5px;
  background: #D81700;
  color: #FEFEFE;
  font-family: Inter;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.3px;
//   margin-bottom: ${p => p.mb !== undefined ? `${p.mb}px!important` : 0};
//   margin-right: ${p => p.mr !== undefined ? `${p.mr}px!important` : '16px'};
  margin: 0 0 ${p => p.mb !== undefined ? `${p.mb}px!important` : 0} 0;

    @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
        font-size: 13px;
        line-height: 16px;
    }

    @media (max-width: ${LayoutConst.screen.m}px) {
        margin-bottom: ${p => p.mb !== undefined ? `${p.mb}px!important` : 0};
        margin-right: ${p => p.mr !== undefined ? `${p.mr}px!important` : '8px'};
    }

    @media (max-width: ${LayoutConst.screen.s - 1}px) {
        padding: 4px 12px;
        margin: 0 0 8px 0;
    }
`

export const EmptyLink = styled.div`
  max-width: 100%;
  line-height: 0.8em;
  margin-bottom: 0 !important;
  margin-right: 0 !important;
  text-decoration: none !important;

  @media (max-width: ${LayoutConst.screen.m}px) {
    &:last-child {
      margin-bottom: 6px !important;
    }
  }
`;

const ProjectPageNew: FC<PageProps> = ({ className, projectId, isLocalPreview }) => {
    const paramsId = useGetFriendlyUrlParam("id");
    const id = projectId || paramsId;
    const theme1 = useTheme();
    const isMobile = useIsMobile();
    const [t, setT] = useState(Date.now());
    const dispatch = useDispatch();
    const profile = useAssociatedProfile();
    const { project: loadedProject, isLoading: isProjectLoading, error: projectError } = useGetProject(id, id + t)
    const [projectSource, setProjectSource] = useState<any>(undefined)
    const [project, setProject] = useState<Project>();
    const [projectStatusFromList, setProjectStatusFromList] = useState<number | null>(null);
    const [hasLgGoods, setHasLgGoods] = useState(false);
    const [lgCoverTextPost, setLgCoverTextPos] = useState<string[]>([]);
    const { i18n } = useTranslation();
    const isProjectPreview = !!useRouteMatch("/preview");
    const isProjectInBookPreview = !!useRouteMatch(["/books/preview", "/book-preview"]);
    const isPreview = isProjectPreview || isProjectInBookPreview || isLocalPreview;
    const isBookProjectPreview = !!useRouteMatch(["/books/book-preview", '/book-preview']);
    const { voteEnd, onToggleStatus } = useVoteContest();
    //const history = useHistory();

    // useEffect(() => {
    //     if (!projectId && !!project && !!urlPath) {
    //         if (!!project.friendlyURLPath && project.friendlyURLPath !== urlPath) {
    //             history.replace(`${CreateLinkTo.projectNew(project)}${window.location.search}${window.location.hash}`);
    //         }
    //     }
    // }, [project, projectId, history, urlPath]);

    const computedProject = useMemo(() => computeProject(project, ImageRequest.image), [project]);

    const isMac13 = useBreakpoint(LayoutConst.screen.mac13);
    const is992 = useBreakpoint(LayoutConst.screen.m);
    const is744 = useBreakpoint(LayoutConst.screen.s);
    const is743 = useBreakpoint(743);
    const is1200 = useBreakpoint(LayoutConst.screen.l);
    const vtype = useSelector((state: any) => state.vt.projectVType);
    const isOutsideTextPosition = useSelector((state: any) => state.vt.isOutsideTextPosition);

    const currentTeam = useGetCurrentTeam();

    const isBooks = AppTester.isBooks();
    const isAgeyTomesh = AppTester.isAgeytomesh();
    const isOnlines = AppTester.isOnlines();
    const isSuperPuper = AppTester.isSuperPuper();
    const isGeograffee = AppTester.isGeograffee();
    const isCGrave = AppTester.isCGRAVE();
    const isMediiia = AppTester.isMediiia();
    const isAdcrDafesNet = AppTester.isAdcrDafesNet();
    const isContinuumSite = AppTester.isContinuumSite();
    const isGameDeziiign = AppTester.isGameDeziiign();
    const isDafes = AppTester.isGeoDafes();
    const isProHSE = AppTester.isProHSE();

    const isPaidBook: any = useMemo(() => BookDelegate.isPaid(project?.book || project) && (project?.isBook || project?.book), [project])

    const bookPaymentData = useGetBookPaymentData(project)
    // const {additionalItems: collectionReprod} = useGetBookPaymentData(project, true)
    const { additionalItems, isLoadingCard } = bookPaymentData

    // const [collectionReprod] = useGetAdditionalItems(project?.book?.bookId || project?.projectId, isPaidBook, Boolean(project?.book?.bookId || project?.isBook))


    // @ts-ignore
    const hasCreators = Boolean(project?.entitiesCreators?.length)
    const creatorsLoading = isProjectLoading

    const currentTeamId = useMemo(() => {
        // @ts-ignore
        const teams = project?.teams;
        if (!teams || teams?.length === 0) return '';

        return teams?.find((team: any) => team?.moderationType === ModerationType.LMS)?.teamId || ''
    }, [project])
    const { colors: disciplineColors } = useGetLMSDisciplines(currentTeamId)

    
    const { scrollToElement } = useInstantScroll({
        navbarHeight: 64,
        anchorMenuHeight: is744 ? 0 : 50,
        extraOffset: is744 ? 70 : 16,
        minDuration: 2000,
        maxDuration: 2000,
        scrollSpeed: 1.0,
    });

    useEffect(() => {
        const handleInitialScroll = () => {
            setTimeout(() => {
                const hash = window.location.hash.replace('#', '');
                if (hash) {
                    scrollToElement(hash, false);
                }
            }, 100);
        };

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', handleInitialScroll);
        } else {
            handleInitialScroll();
        }

        return () => {
            document.removeEventListener('DOMContentLoaded', handleInitialScroll);
        };
    }, [scrollToElement]);

    const competitionIds = useMemo(() => {
        //@ts-ignore
        return project?.competitionApproves?.map(({ competitionId }) => competitionId);
    }, [project])

    const isLmsModerationType = useMemo(
        //@ts-ignore
        () => (project?.teams?.length && project?.teams.some((i) => i?.moderationType === ModerationType.LMS)), [project]);

    const isBookView = useRouteMatch("/books/book");

    const setPagerContent = (project: Project) => {
        dispatch(chapterPagerSlice.actions.setChapter(project))
    }

    const [{ voteCollectionId, voteEndParam, currentCompetitionId, tab }, setQueryParams] = useQueryState();

    const isLMS = useMemo(
        () => ((project?.contexts?.includes('lmshseproject') || project?.contexts?.includes('lmsproject')) && isLmsModerationType), [project, isLmsModerationType])

    const computeAdditionaLmsData = useMemo(() => {
    return {
        //@ts-ignore
        contentLink: project?.additionalInfo?.contentLink || '',
             //@ts-ignore
        detailsLink: executeLmsButtonPath(project?.teams?.[0]?.buttons) || '',
             //@ts-ignore
        backLink: project?.teams?.[0]?.route || ''
    }
    } , [project])


    const {
        LMSModuleInfo: lmsModuleInfo,
        isLoading: lmsModuleInfoLoading
    } = useGetLMSModule(project?.projectId || "", computeAdditionaLmsData, !isLMS)

    const isOwner = useMemo(() => {
        if (!project) return undefined
        return profile ? (project?.authorId === profile?.accountId || project?.coAuthorIds?.includes(profile?.accountId) || (lmsModuleInfo?.teacherIds || [])?.includes(profile?.accountId)) : false
    }, [project, profile, lmsModuleInfo])

    const isModer = useMemo(() => profile && (ProfileUtils.isModer(profile) || ProfileUtils.isMiddleModer(profile)), [profile]);
    const isAuthorized = !!profile;

    const {
        competitionsWinner,
        competitionsParticipant,
        competitionInfo,
        competitionFynalist,
        competitionLMSInfo,
        competitionsExamCompleted
    } = useGetCompetitionList(competitionIds, id, isOwner || isModer, t);

    const isOwnerLoading = useMemo(() => (isOwner !== undefined && (!isLMS && !lmsModuleInfoLoading)), [isOwner, isLMS, lmsModuleInfo, lmsModuleInfoLoading]);

    const isProjectVotingOver = useMemo(
        () => (voteEnd === PROJECT_VOTE_STATUS.end && !voteEndParam && currentCompetitionId)
        , [voteEnd, voteEndParam, currentCompetitionId]);

    const isExamProject = useMemo(() => !!competitionLMSInfo?.length, [competitionLMSInfo]);

    const showVoteEnd = useMemo(() => 
        !isExamProject && 
        isProjectVotingOver && 
        competitionInfo && 
        competitionInfo.id && 
        competitionInfo.name, 
        [isExamProject, isProjectVotingOver, competitionInfo]
    );

    const onUpdate = (t: number) => setT(t);

    const isCollectionVote = useMemo(() => (project?.contexts?.includes(SP_VOTE_COLLECTION_SIGN)), [project])

    const usercolor = useMemo(() => {
        const p: any = project;
        if (project && p.additionalInfo && p.additionalInfo.color) {
            return p.additionalInfo.color;
        }
        return (AppTester.isDeziiign() || isDafes) ? Colors.gal : Colors.geo;
    }, [project, isDafes]);

    const calculatedColor = useMemo(() => {
        if (isMobile && hasCreators && isMediiia) {
            return '#e7e7e7';
        }
        if (isLMS) {
            return '#d2d4d3';
        }
        if (isGeograffee && !isDafes) {
            return Colors.geo;
        }
        if (isMobile && !(isSuperPuper || isCollectionVote || isCGrave || isContinuumSite || isAdcrDafesNet || isMediiia)) {
            return Colors.gal;
        }
        if (isSuperPuper || isCollectionVote || isContinuumSite || isAdcrDafesNet) {
            return '#000000';
        }

        if (isGameDeziiign && usercolor === Colors.geoWhite100) {
            return '#2A3034'
        }
        return usercolor;
    }, [isGeograffee, isMobile, hasCreators, usercolor, isCGrave, isDafes, isContinuumSite, isCollectionVote, isGameDeziiign, isMediiia]);

    const { theme } = createThemeByColor(calculatedColor, theme1);

    console.log('112112', calculatedColor)

    useEffect(() => {
        if (project?.author) {
            if (project?.author?.globalTypes?.includes('Employee')
                || project?.author?.globalTypes?.includes('Cooperator')
                || project?.author?.globalTypes?.includes('Student')
                || project?.author?.globalTypes?.includes('hsealumni')) {
                dispatch(NavbarSearchActions.setDesignerType(true));
            } else {
                dispatch(NavbarSearchActions.setDesignerType(false));
            }
        }
        const viewType = project?.longread?.viewType || 'container';
        dispatch(setProjectVType(viewType))
        return () => {
            dispatch(NavbarSearchActions.setDesignerType(true));
            dispatch(setProjectVType('container'))
        }
    }, [project?.author])

    useEffect(() => {
        if (project) {
            dispatch(InfinityProjectsActions.setIsProjectPage(true))
            dispatch(CurrentProjectActions.setOriginalsState(false))
            dispatch(CurrentProjectActions.setIsOstBay(getOstbay(project)))
        }
    }, [project])

    useEffect(() => {
        //@ts-ignore
        const teamId = project?.teams?.[0]?.teamId;

        if (teamId) {
            API.request()
                .endpoint(Endpoints.cardsMnePw)
                .url('/api/Products/GetList')
                .get()
                .addQuery('teamId', teamId)
                .addQuery('isHidden', 'false')
                .send()
                .then((r) => r.json())
                .then((r) => {
                    setHasLgGoods(r?.data?.length > 0)
                })
        }
    }, [project])

    useEffect(() => {
        if (loadedProject) {
            setProjectSource(loadedProject)
            setPagerContent(loadedProject)
        }
    }, [loadedProject]);

    useEffect(() => {
        if (projectError === 403 && id && !projectStatusFromList) {
            ProjectAPI.GetList({
                projectIds: [id],
                projectFilter: 1,
                isLite: true,
                isContextIgnored: true,
                limit: 1
            })
                .then((r) => r.json())
                .then((r) => {
                    if (r?.data && Array.isArray(r.data) && r.data.length > 0) {
                        const projectFromList = r.data[0];
                        if (projectFromList && typeof projectFromList === 'object' && projectFromList.status !== undefined) {
                            setProjectStatusFromList(projectFromList.status);
                        }
                    }
                })
                .catch(() => {});
        }
    }, [projectError, id, projectStatusFromList]);

    const getCoverTextPosition = (id: string) => {
        if (id) {
            API.request()
                .endpoint(Endpoints.longread)
                .url('api/post/GetMany')
                .addQuery('idUser', '2')
                .addQuery('idLongread', id)
                .get()
                .send()
                .then((r) => r.json())
                .then((r) => setLgCoverTextPos(
                    getLongreadCoverTextPosition(r)
                ))
        }
    }

    useEffect(() => {
        dispatch(setIsOutsideTextPosition(lgCoverTextPost?.includes('outside') || !lgCoverTextPost || lgCoverTextPost?.length === 0));
    }, [lgCoverTextPost, dispatch])

    useEffect(() => {
        if (project) {
            getCoverTextPosition(project?.longreadId)
        }
    }, [project])

    useDeepCompareEffect(() => {
        setProject(API.toLang(projectSource, i18n.language));
    }, [projectSource, i18n.language]);

    if (showVoteEnd) {
        return <VoteEnd text={competitionInfo?.name} link={competitionInfo?.id}
            // @ts-ignore
            onToggleStatus={(flag: boolean) => {
                setQueryParams?.({ voteEndParam: true })
                onToggleStatus(flag)
            }} />
    }

    if (isPreview && projectError === 403) {
        return <ProjectEditFrameForbidden id={id} />;
    }

    if (((isPreview || isBookProjectPreview) && isOwnerLoading && !isOwner && !isModer)) {
        return <ErrorNotPreviewPermission id={id} />;
    }

    if (isProjectLoading) {
        return (
            <Overback
                className={className}
                state={isProjectLoading}
                component={ProjectDisplayLayout}
            />
        );
    }

    const projectToCheck = project || loadedProject;
    if (projectToCheck && typeof projectToCheck === 'object' && projectToCheck.status === 3 && projectToCheck.projectId && !isOwner && !isModer) {
        return <BlockedProjectModal />;
    }

    if (ProjectStatusDeleted(project)) {
        return <Unavailable />;
    }

    if (
        project && (ProjectStatusDrafts(project)) &&
        (!isPreview && !isBookProjectPreview && isOwnerLoading && isOwner) &&
        !isModer) {
        return <ProjectEditFrameForbidden id={id} />;
    }
    
    if (projectToCheck && typeof projectToCheck === 'object' && 
        projectToCheck.status === 3 && 
        projectToCheck.projectId && 
        !isOwner && !isModer) {
        return <BlockedProjectModal />;
    }

    if (ProjectStatusDeleted(project)) {
        return <Unavailable />;
    }

    if (projectError === 403) {
        if (projectStatusFromList === 0) {
            if (isBookView) return <BookFrameError id={id} />
            return <ProjectFrameError id={id} />;
        }
        
        if (projectStatusFromList === 3) {
            return <BlockedProjectModal />;
        }
        
        if (isBookView) return <BookFrameError id={id} />
        return <ProjectFrameError id={id} />;
    }
    
    if (projectError === 404) {
        const projectToCheck = project || loadedProject;
        if (projectToCheck && typeof projectToCheck === 'object' && projectToCheck.status === 3 && projectToCheck.projectId) {
            return <BlockedProjectModal />;
        }
        return <NotFoundPage />
    }
    
    if (projectError) {
        if (isBookView) return <BookFrameError id={id} />
        return <ProjectFrameError id={id} />;
    }

    if (!project) {
        return null;
    }
    if (project.status === 404) {
        return <Unavailable />;
    }

    return (
        <ProjectContent
            projectId={id}
            project={project}
            setProject={setProject}
            tab={tab}
            voteCollectionId={voteCollectionId}
            voteEndParam={voteEndParam}
            currentCompetitionId={currentCompetitionId}
            setQueryParams={setQueryParams}
            isProjectLoading={isProjectLoading}
            projectSource={projectSource}
            computedProject={computedProject}
            setProjectSource={setProjectSource}
            isOwner={isOwner}
            isModer={isModer}
            isOwnerLoading={isOwnerLoading}
            profile={profile}
            lmsModuleInfo={lmsModuleInfo}
            isBookProjectPreview={isBookProjectPreview}
            isProjectPreview={isProjectPreview}
            isPreview={isPreview}
            competitionsExamCompleted={competitionsExamCompleted}
            competitionsParticipant={competitionsParticipant}
            competitionsWinner={competitionsWinner}
            competitionFynalist={competitionFynalist}
            isMac13={isMac13}
            is992={is992}
            is744={is744}
            is743={is743}
            is1200={is1200}
            vtype={vtype}
            isOutsideTextPosition={isOutsideTextPosition}
            onUpdate={onUpdate}
            hasLgGoods={hasLgGoods}
            lgCoverTextPost={lgCoverTextPost}
            currentTeam={currentTeam}
            bookPaymentData={bookPaymentData}
            additionalItems={additionalItems}
            collectionReprod={additionalItems}
            isBooks={isBooks}
            isAgeyTomesh={isAgeyTomesh}
            isOnlines={isOnlines}
            isSuperPuper={isSuperPuper}
            isGeograffee={isGeograffee}
            isCGrave={isCGrave}
            isAdcrDafesNet={isAdcrDafesNet}
            isContinuumSite={isContinuumSite}
            isGameDeziiign={isGameDeziiign}
            isDafes={isDafes}
            isProHSE={isProHSE}
            isLoadingCard={isLoadingCard}
            disciplineColors={disciplineColors}
            theme={theme}
            calculatedColor={calculatedColor}
            isCollectionVote={isCollectionVote}
            isMobile={isMobile}
            isLmsModerationType={isLmsModerationType}
            isLMS={isLMS}
            onlyContent={!!projectId}
        />
    )
};

export default memo(ProjectPageNew);
